import { format as formatDate } from 'date-fns';
import i18n from '@/i18n';
import { getRequestLanguage } from '@/utils/requestLanguage';

/** Kinds of downloaded file whose name follows the selected language (keys of `common:downloadFilenames`). */
export type DownloadFileKind =
  | 'salesReport'
  | 'inventoryReport'
  | 'customerReport'
  | 'financialReport'
  | 'dailySummary'
  | 'transactions'
  | 'expenses'
  | 'customers'
  | 'customerImportTemplate'
  | 'stockAudit'
  | 'auditLogs'
  | 'outletCash'
  | 'users'
  | 'storeDataExport'
  | 'storeBackup'
  | 'receipt'
  | 'settingsComparison'
  | 'expenseReceipt';

/** Options of {@link getDownloadBaseName} and {@link buildDownloadFilename}. */
export interface DownloadFilenameOptions {
  /** Date part of the name: a Date or an already formatted `yyyy-MM-dd` string (default: today) */
  date?: Date | string;
  /** Identifier appended to the base name, e.g. an audit code; reduced to ASCII letters, digits and hyphens */
  suffix?: string;
  /** Leave the date out of the name, e.g. for import templates */
  omitDate?: boolean;
}

/**
 * Reduce free text to a filesystem-safe ASCII fragment.
 * @param value - Text to clean
 * @returns Letters, digits, dots, underscores and hyphens only; other characters become hyphens
 */
export const toSafeFilenamePart = (value: string): string =>
  value
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Name of a downloaded file in the selected language, without extension:
 * `{base name}[-{suffix}]_{yyyy-MM-dd}`, e.g. `laporan-penjualan_2026-10-19` or `sales-report_2026-10-19`.
 * @param kind - Kind of file
 * @param options - Date and suffix of the name
 * @returns ASCII, filesystem-safe base name
 */
export const getDownloadBaseName = (
  kind: DownloadFileKind,
  options: DownloadFilenameOptions = {}
): string => {
  const base = i18n.t(`downloadFilenames.${kind}`, { ns: 'common', lng: getRequestLanguage() });
  const suffix = options.suffix ? toSafeFilenamePart(options.suffix) : '';
  const named = suffix ? `${base}-${suffix}` : base;
  if (options.omitDate) return named;
  const date =
    typeof options.date === 'string'
      ? options.date
      : formatDate(options.date ?? new Date(), 'yyyy-MM-dd');
  return `${named}_${date}`;
};

/**
 * File name of a download in the selected language.
 * @param kind - Kind of file
 * @param extension - File extension without the dot (e.g. "pdf", "xlsx", "csv")
 * @param options - Date and suffix of the name
 * @returns File name such as `laporan-penjualan_2026-10-19.pdf`
 */
export const buildDownloadFilename = (
  kind: DownloadFileKind,
  extension: string,
  options: DownloadFilenameOptions = {}
): string => `${getDownloadBaseName(kind, options)}.${extension}`;

/** File extension of each content type an expense receipt can have. */
const RECEIPT_EXTENSIONS: Record<string, string> = {
  'application/pdf': 'pdf',
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'image/gif': 'gif',
};

/**
 * File name of a downloaded expense receipt in the selected language, without a date:
 * `{expense receipt}-{receipt number or short expense ID}.{extension}`,
 * e.g. `bukti-pengeluaran-INV-0042.jpg` or `expense-receipt-INV-0042.jpg`.
 * @param expense - Expense the receipt belongs to
 * @param contentType - Content type of the receipt file; unknown types fall back to "jpg"
 * @returns ASCII, filesystem-safe file name
 */
export const buildExpenseReceiptFilename = (
  expense: { id: string; receiptNumber?: string | null },
  contentType?: string | null
): string => {
  const mimeType = (contentType || '').split(';')[0].trim().toLowerCase();
  return buildDownloadFilename('expenseReceipt', RECEIPT_EXTENSIONS[mimeType] ?? 'jpg', {
    suffix: expense.receiptNumber || expense.id.substring(0, 8),
    omitDate: true,
  });
};
